import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { DONATE_BADGE_PLACEHOLDER, donateBadgeSnippet } from '../lib/donateBadge';
import { Field } from './Field';
import { Button } from './ui/Button';

// "Put this in your README" (arc 101 slice 11) — the growth loop's one artifact,
// rendered wherever a maintainer is looking at donations: the inbox empty state
// (account-wide, so the placeholder) and a repo's "accept donations" panel (that
// project, already filled in).
//
// The description follows the SLUG, not the caller. A snippet carrying a real
// `owner/repo` must not tell its reader to swap a placeholder that isn't there,
// and a caller free to pair either line with either slug would eventually pair
// them wrong.

export function DonateBadgeSnippet(props: {
  /** `owner/repo`, or `DONATE_BADGE_PLACEHOLDER` where no single project applies. */
  readonly slug: string;
  /** Layout modifier for the host surface (the block itself is context-free). */
  readonly class?: string;
}): JSX.Element {
  const [copied, setCopied] = createSignal(false);
  const snippet = (): string => donateBadgeSnippet(props.slug);
  const generic = (): boolean => props.slug === DONATE_BADGE_PLACEHOLDER;
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copy = (): void =>
    void navigator.clipboard?.writeText(snippet()).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  return (
    <div class={props.class ? `donate-badge ${props.class}` : 'donate-badge'}>
      {/* The snippet is a read-out to copy, not a control: an <output>, which the
          heading names as its label. */}
      <Field
        label={t('donations.badge_heading')}
        id={`donate-badge-${props.slug.replace(/[^A-Za-z0-9_-]/g, '-')}`}
        desc={
          generic()
            ? t('donations.badge_desc')
            : t('donations.badge_desc_repo', { project: props.slug })
        }
      >
        {(a11y) => (
          <output {...a11y} class="code-block">
            {snippet()}
          </output>
        )}
      </Field>
      <Button variant="outline" size="sm" onClick={copy}>
        <IconCopy size="0.875rem" />
        {t(copied() ? 'donations.badge_copied' : 'donations.badge_copy')}
      </Button>
    </div>
  );
}
